import type { GeoLocation } from '@smg-real-estate/geo-libs';
import { Country, OfferType } from '@smg-real-estate/hg-rets-schema';
import { defineStore } from 'pinia';
import { computed, ref, watch, type Ref } from 'vue';

import { TopCategory } from '@/helpers/listingUtils';
import { isSharedApartmentTopCategoryAvailable } from '@/helpers/sharedApartment';
import { CustomerSegment } from '@/models/customerSegments';

interface LandingPickerRefs {
  stagedCustomerSegment: Ref<CustomerSegment | null>;
  customerSegment: Ref<CustomerSegment | null>;
  offerType: Ref<OfferType | null>;
}

function createLandingPickerActions(refs: LandingPickerRefs) {
  function clearStagedCustomerSegment() {
    refs.stagedCustomerSegment.value = null;
  }

  function selectCustomerSegment(customerSegmentValue: CustomerSegment) {
    clearStagedCustomerSegment();

    if (customerSegmentValue === CustomerSegment.TENANT) {
      refs.customerSegment.value = CustomerSegment.TENANT;
      refs.offerType.value = OfferType.Rent;
      return;
    }

    if (!refs.offerType.value) {
      return;
    }

    refs.customerSegment.value = customerSegmentValue;
  }

  function setOfferType(offerTypeValue: OfferType) {
    const currentSegment = refs.customerSegment.value ?? refs.stagedCustomerSegment.value ?? null;
    clearStagedCustomerSegment();
    const customerSegment = currentSegment === CustomerSegment.TENANT ? null : currentSegment;

    refs.offerType.value = offerTypeValue;

    if (!customerSegment) {
      refs.customerSegment.value = null;
      return;
    }

    refs.customerSegment.value = customerSegment;
  }

  function stageCustomerSegment(customerSegmentValue: CustomerSegment | null) {
    refs.stagedCustomerSegment.value = customerSegmentValue;
  }

  return {
    selectCustomerSegment,
    setOfferType,
    stageCustomerSegment,
    clearStagedCustomerSegment,
  };
}

function createLandingStoreState() {
  const stagedCustomerSegment = ref<CustomerSegment | null>(null);
  const customerSegment = ref<CustomerSegment | null>(null);
  const offerType = ref<OfferType | null>(null);
  const listingCategory = ref<TopCategory | ''>('');
  const listingType = ref('');
  const selectedCountry = ref(Country.Ch);
  const zip = ref('');
  const city = ref('');
  const canton = ref('');
  const geoLocation = ref<null | Partial<GeoLocation>>(null);
  const isSubmittingInsertion = ref(false);

  const pickerActions = createLandingPickerActions({
    stagedCustomerSegment,
    customerSegment,
    offerType,
  });

  // Shared Apartment can stay selected after the dropdown hides it; clear it when it is no longer eligible.
  watch(
    [customerSegment, offerType],
    () => {
      if (listingCategory.value !== TopCategory.SHARED_APARTMENT) {
        return;
      }

      if (
        isSharedApartmentTopCategoryAvailable({
          customerSegment: customerSegment.value,
          offerType: offerType.value,
        })
      ) {
        return;
      }

      listingCategory.value = '';
      listingType.value = '';
    },
    { flush: 'sync' },
  );

  function $reset() {
    stagedCustomerSegment.value = null;
    customerSegment.value = null;
    offerType.value = null;
    listingCategory.value = '';
    listingType.value = '';
    selectedCountry.value = Country.Ch;
    zip.value = '';
    city.value = '';
    canton.value = '';
    geoLocation.value = null;
    isSubmittingInsertion.value = false;
  }

  return {
    stagedCustomerSegment,
    customerSegment,
    offerType,
    listingCategory,
    listingType,
    selectedCountry,
    zip,
    city,
    canton,
    geoLocation,
    isSubmittingInsertion,
    ...pickerActions,
    $reset,
  };
}

export const useLandingStore = defineStore('landing', () => {
  const state = createLandingStoreState();
  const { customerSegment, listingCategory, zip, city, canton, geoLocation, ...rest } = state;

  const isTenantSelected = computed(() => customerSegment.value === CustomerSegment.TENANT);
  const isCountrySwitzerland = computed(() => state.selectedCountry.value === Country.Ch);
  const isOfferTypeBuyDisabled = computed(() => isTenantSelected.value);
  const isSecondaryRoomSelected = computed(() => listingCategory.value === TopCategory.SECONDARY);

  function setCustomerSegment(newCustomerSegment: CustomerSegment | null) {
    customerSegment.value = newCustomerSegment;
  }

  function setLocationInfo(payload: {
    zip: string;
    city: string;
    canton: string;
    geoLocation: Partial<GeoLocation> | null;
  }) {
    zip.value = payload.zip;
    city.value = payload.city;
    canton.value = payload.canton;
    geoLocation.value = payload.geoLocation;
  }

  return {
    customerSegment,
    listingCategory,
    zip,
    city,
    canton,
    geoLocation,
    ...rest,
    isTenantSelected,
    isCountrySwitzerland,
    isOfferTypeBuyDisabled,
    isSecondaryRoomSelected,
    setCustomerSegment,
    setLocationInfo,
  };
});
